import { type ReactNode, useEffect, useMemo, useState } from "react";
import type { Api } from "../api.ts";
import { mocksOn } from "./flags.ts";
import type { MockSources } from "./mock/index.ts";
import { createRealSources, type Sources, SourcesContext } from "./sources.ts";

/**
 * Provides the data sources. Steps, sign-offs, templates and script import are always the real ones (F4);
 * with the mock flag on, billing, Explore, Learn and the Original to remix come from the mocks once their chunk
 * has loaded.
 */
export function SourcesProvider({
  api,
  ws,
  meId,
  children,
}: {
  api: Api;
  ws: string | null;
  meId: string | null | undefined;
  children: ReactNode;
}) {
  const real = useMemo(() => createRealSources(api, ws, meId), [api, ws, meId]);
  const [mock, setMock] = useState<MockSources | null>(null);
  useEffect(() => {
    if (!mocksOn()) return;
    let live = true;
    void import("./mock/index.ts").then((m) => live && setMock(m.createMockSources()));
    return () => {
      live = false;
    };
  }, []);
  const sources = useMemo<Sources>(
    () =>
      mock
        ? {
            ...real,
            mock: true,
            billing: mock.billing,
            explore: mock.explore,
            learn: mock.learn,
            start: { ...real.start, original: mock.original },
          }
        : real,
    [real, mock],
  );
  return <SourcesContext.Provider value={sources}>{children}</SourcesContext.Provider>;
}
